import { z } from 'zod';

const ZeroOneSchema = z.union([z.literal('0'), z.literal('1'), z.string()]).nullable();

export enum PersonaKycStatus {
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  PENDING = 'PENDING',
}

export const ClientTradesySchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumbers: z.union([z.string(), z.number()]),
  isMigrated: ZeroOneSchema,
  sellerMigrationStatus: z.string(),
  sellerMigrationDate: z.string(),
  plannedSellerMigrationDate: z.string(),
  activeListingsOnTradesy: z.number(),
  eligibleListings: z.union([z.string(), z.number()]),
  publishedListingsOnVC: z.union([z.string(), z.number()]),
  cashableBalance: z.union([z.string(), z.number()]),
  noncashableBalance: z.union([z.string(), z.number()]),
  addresses: z.string(),
});
export type ClientTradesy = z.infer<typeof ClientTradesySchema>;

export const ClientPromoCodeSchema = z.object({
  code: z.string(),
  currency: z.string(),
  amount: z.union([z.string(), z.number()]),
  discountPercent: z.string(),
  orderNumber: z.string(),
  status: z.string(),
});
export type ClientPromoCode = z.infer<typeof ClientPromoCodeSchema>;

export const CustomerGetPaidSchema = z.object({
  accountState: z.string().nullish(),
  transferMethodState: z.string().nullish(),
  accountType: z.string().nullish(),
  bankName: z.string().nullish(),
  accountDetail: z.string().nullish(),
  kycStatus: z.string().nullish(),
  isPayoutBlocked: z.boolean(),
  payoutBlockedReason: z.string().nullish(),
  accountCurrency: z.string().nullish(),
});

export const ClientExtraPayoutSchema = z.object({
  amount: z.string().nullish(),
  currency: z.string().nullish(),
  clientPaymentId: z.string().nullish(),
  payoutStatus: z.string().nullish(),
  paymentDate: z.string().nullish(),
});

export const ClientSchema = z.object({
  accountCreatedAt: z.string(),
  accountStatus: z.string(),
  address: z.string(),
  clientId: z.string(),
  clientInfoStatus: z.string(),
  clientInfoDescription: z.string(),
  clientShippingAddressId: z.number().nullable(),
  clientStatus: z.string().nullable(),
  customerStatusColor: z.string(),
  companyName: z.string(),
  country: z.string(),
  csScore: z.number().nullable(),
  csScoreV2History: z.array(
    z.object({
      csScoreV2: z.number(),
      datetimeCalculated: z.string(),
    }),
  ),
  currency: z.string(),
  customerId: z.string(),
  customerType: z.string(),
  email: z.string(),
  flags: z.string().array(),
  isActive: ZeroOneSchema,
  isTradesyMigrated: ZeroOneSchema,
  isTransferAuthorized: z.boolean(),
  language: z.string(),
  mobile: z.string(),
  name: z.string(),
  phone: z.union([z.string(), z.number()]),
  promoCodes: ClientPromoCodeSchema.array(),
  salesNumber: z.union([z.string(), z.number()]),
  tradesy: ClientTradesySchema.optional(),
  zendeskId: z.string(),
  paidAccountId: z.string().nullish(),
  paidAccountType: z.string().nullish(),
  emailSubscribed: z.boolean().nullable(),
  awaitingPhoneValidation: z.boolean().nullable(),
  purchasesCount: z.number().nullable().optional(),
  totalPurchasedAmount: z.number().nullable().optional(),
  emailUnsubscribedDate: z.string().nullish(),
  siftScore: z.number().nullable().optional(),
  totalGMV: z.number().nullable().optional(),
  counterfeitSaleCount: z.string().nullable().optional(),
  counterfeitItems: z.string().array().optional(),
  refusedItemsCount: z.number().optional(),
  verifiedPhoneNumber: z.string().nullable().optional(),
  customerGetPaid: CustomerGetPaidSchema.nullish(),
  referralCodes: z.array(z.string()).nullable().optional(),
  extraPayouts: z.array(ClientExtraPayoutSchema).nullable().optional(),
  personaKycStatus: z.string().nullish().optional(),
});
export type Client = z.infer<typeof ClientSchema>;

export const ClientActionHistoSchema = z.object({
  superuser: z.string(),
  action: z.string(),
  comment: z.string(),
  date: z.string(),
  status: z.number(),
});

export const ClientActionHistosSchema = z.object({
  data: ClientActionHistoSchema.array(),
  total: z.number(),
});
export type ClientActionHistos = z.infer<typeof ClientActionHistosSchema>;

export const ClientSiftScoreSchema = z.object({
  siftScore: z.number().nullable(),
});
export type ClientSiftScore = z.infer<typeof ClientSiftScoreSchema>;

export const ClientLastLoginAttemptSchema = z.object({
  lastLoginAttemptCountry: z.string().nullable().optional(),
});
export type ClientLastLoginAttempt = z.infer<typeof ClientLastLoginAttemptSchema>;

export const ClientChatModerateStatsSchema = z.object({
  reasonLastBan: z.string().nullable().optional(),
  nbReports: z.string().nullable().optional(),
  nbBans: z.string().nullable().optional(),
  nbUnbans: z.string().nullable().optional(),
});
export type ClientChatModerateStats = z.infer<typeof ClientChatModerateStatsSchema>;

export const ClientBadgesOverviewSchema = z.object({
  currentBadgeName: z.string().nullish().optional(),
  currentBadgeId: z.string().nullish().optional(),
  qualityRate: z.string().nullish().optional(),
  authenticationRate: z.string().nullish().optional(),
  litigations: z.string().nullish().optional(),
  cancellationOnTime: z.string().nullish().optional(),
  totalCancellation: z.string().nullish().optional(),
  shippedOnTime: z.string().nullish().optional(),
  totalShipped: z.string().nullish().optional(),
  recentSoldProducts: z.string().nullish().optional(),
  isExcluded: z.string().nullish().optional(),
  shippingRate: z.string().nullish().optional(),
  updatedAt: z.string().nullish().optional(),
});
export type ClientBadgesOverview = z.infer<typeof ClientBadgesOverviewSchema>;

export const ClientBadgesProductDetailSchema = z.object({
  productId: z.string().nullish().optional(),
  removedFromSale: z.string().nullish().optional(),
  compliant: z.string().nullish().optional(),
  counterfeit: z.string().nullish().optional(),
  notCompliant: z.string().nullish().optional(),
  received: z.string().nullish().optional(),
  shipped: z.string().nullish().optional(),
  notReceived: z.string().nullish().optional(),
  notReceivedRestored: z.string().nullish().optional(),
  receivedDate: z.string().nullish().optional(),
  isVip: z.string().nullish().optional(),
  segment: z.string().nullish().optional(),
});
export type ClientBadgesProductDetail = z.infer<typeof ClientBadgesProductDetailSchema>;

export const ClientBadgesShippingDetailSchema = z.object({
  productId: z.string().nullish().optional(),
  productName: z.string().nullish().optional(),
  soldDate: z.string().nullish().optional(),
  shippedInTime: z.string().nullish().optional(),
  daysToShip: z.string().nullish().optional(),
  directShipping: z.string().nullish().optional(),
  shippingType: z.string().nullish().optional(),
  pickupDate: z.string().nullish().optional(),
});
export type ClientBadgesShippingDetail = z.infer<typeof ClientBadgesShippingDetailSchema>;

export const ClientBadgesSchema = z.object({
  overview: ClientBadgesOverviewSchema.nullish().optional(),
  productDetails: ClientBadgesProductDetailSchema.array().nullish().optional(),
  productTotal: ClientBadgesProductDetailSchema.nullish().optional(),
  shippingDetails: ClientBadgesShippingDetailSchema.array().nullish().optional(),
  shippingTotal: ClientBadgesShippingDetailSchema.nullish().optional(),
});
export type ClientBadges = z.infer<typeof ClientBadgesSchema>;

export const ClientSalesReportSchema = z.object({
  year: z.number(),
  file_path: z.string().nullish(),
  presigned_url: z.string().nullish(),
});
export type ClientSalesReport = z.infer<typeof ClientSalesReportSchema>;

export const ClientCanceledSalesDataSchema = z.object({
  productId: z.number().nullish(),
  canceledDate: z.string().nullish(),
});
export type ClientCanceledSalesData = z.infer<typeof ClientCanceledSalesDataSchema>;

export const ClientCanceledSalesSchema = z.object({
  total: z.number().nullish(),
  data: ClientCanceledSalesDataSchema.array(),
});
export type ClientCanceledSales = z.infer<typeof ClientCanceledSalesSchema>;

export const ClientStatsSchema = z.object({
  totalSold: z.union([z.string(), z.number()]).nullish(),
  totalShipped: z.union([z.string(), z.number()]).nullish(),
  totalCancelled: z.union([z.string(), z.number()]).nullish(),
  message: z.string().nullish(),
});

export type ClientStatsResponse = z.infer<typeof ClientStatsSchema>;

export const ClientReliabilityStatsSchema = z.object({
  totalGMV: z.number().nullable(),
  refusedItemsCount: z.number().nullable(),
  totalPurchasedAmount: z.number().nullable(),
  counterfeitSaleCount: z.number().nullable(),
  counterfeitItems: z.string().array(),
});
export type ClientReliabilityStats = z.infer<typeof ClientReliabilityStatsSchema>;

export const PersonalKycDataSchema = z.object({
  kycRequestId: z.string().nullish(),
  internalCustomerId: z.number().nullish(),
  orderId: z.number().nullish(),
  productId: z.number().nullish(),
  status: z.string().nullish(),
  createdAt: z.string().nullish(),
  decidedAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  customerType: z.string().nullish(),
  requestType: z.string().nullish(),
  inquiryId: z.string().nullish(),
  requestTypeDisplayText: z.string().nullish(),
});
export const PersonalKycSchema = z.object({
  total: z.number().nullish(),
  data: PersonalKycDataSchema.array(),
});
export type PersonalKyc = z.infer<typeof PersonalKycSchema>;
